import React, { useEffect, useState } from 'react';
import shuffle from 'lodash.shuffle';
import { useTranslation } from 'react-i18next';

const FillInTheBlank = ({ events }) => {
  const { t } = useTranslation();
  const [shuffledEvents, setShuffledEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [missingWord, setMissingWord] = useState('');
  const [userInput, setUserInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  useEffect(() => {
    if (!events.length) return;
    const shuffled = shuffle(events.filter(e => e.description && e.title && e.year)).slice(0, 10);
    setShuffledEvents(shuffled);
  }, [events]);

  useEffect(() => {
    if (shuffledEvents.length > 0 && currentIndex < shuffledEvents.length) {
      generateQuestion(shuffledEvents[currentIndex]);
    } else if (shuffledEvents.length > 0 && currentIndex >= shuffledEvents.length) {
      setGameOver(true);
    }
  }, [shuffledEvents, currentIndex]);

  const generateQuestion = (event) => {
    const words = event.description.split(' ').filter(w => w.length > 3);

    const importantWords = words.filter(word => {
      const cleanWord = word.replace(/[^a-zA-Z]/g, '');
      return (
        cleanWord.length > 4 &&
        cleanWord[0] === cleanWord[0].toUpperCase() &&
        !["The", "And", "From", "Into", "Upon", "Were", "With", "Over"].includes(cleanWord)
      );
    });

    const randomWord = importantWords.length > 0
      ? importantWords[Math.floor(Math.random() * importantWords.length)]
      : words[Math.floor(Math.random() * words.length)];

    const blankDescription = event.description.replace(randomWord, '____');

    setCurrentEvent({
      title: event.title,
      description: blankDescription,
      fullDescription: event.description,
      year: event.year,
    });
    setMissingWord(randomWord);
    setUserInput('');
    setSubmitted(false);
    setResult(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    const cleanInput = userInput.trim().toLowerCase().replace(/[^a-z]/g, '');
    const cleanAnswer = missingWord.trim().toLowerCase().replace(/[^a-z]/g, '');

    if (cleanInput === cleanAnswer) {
      setResult(true);
      setScore(prev => prev + 1);
    } else {
      setResult(false);
    }
  };

  const handleNext = () => {
    setCurrentIndex(prev => prev + 1);
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setScore(0);
    setGameOver(false);
    setShuffledEvents(shuffle(events));
  };

  if (!currentEvent) {
    return <div className="text-center text-gray-400 mt-10">{t('quiz.fill.loading')}</div>;
  }

  if (gameOver) {
    return (
      <div className="max-w-xl mx-auto p-6 bg-gray-900 text-white rounded-lg shadow-xl mt-10 text-center">
        <h2 className="text-3xl font-bold text-purple-400 mb-4">{t('quiz.fill.gameOver')}</h2>
        <p className="text-lg text-green-400 mb-6">
          {t('quiz.fill.scoreMessage', { score, total: shuffledEvents.length })}
        </p>
        <button
          onClick={handleRestart}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
        >
          🔄 {t('quiz.fill.playAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 bg-gray-900 text-white rounded-lg shadow-xl mt-10">
      <h2 className="text-2xl font-bold mb-6 text-purple-400 text-center">
       {t('quiz.mode.fillInBlank')} ({currentIndex + 1}/{shuffledEvents.length})
      </h2>

      <div className="bg-gray-800 p-6 rounded-lg mb-6">
        <h3 className="text-xl font-semibold text-blue-300 mb-2">
          {currentEvent.title} ({currentEvent.year})
        </h3>
        <p className="text-md text-gray-300">{currentEvent.description}</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <input
          type="text"
          placeholder={t('quiz.fill.placeholder')}
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          className="bg-gray-800 border border-gray-600 rounded px-4 py-2 text-white focus:outline-none"
          disabled={submitted}
        />
        {!submitted ? (
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold transition"
          >
            🚀 {t('quiz.fill.submit')}
          </button>
        ) : (
          <div className="text-center space-y-4">
            {result ? (
              <p className="text-green-400 font-bold text-lg">{t('quiz.fill.correct')}</p>
            ) : (
              <p className="text-red-400 font-bold text-lg">
                {t('quiz.fill.incorrect')} <span className="underline">{missingWord}</span>
              </p>
            )}
            <p className="text-gray-400 text-sm">
              {t('quiz.fill.full')}: {currentEvent.fullDescription}
            </p>
            <button
              onClick={handleNext}
              type="button"
              className="bg-green-600 hover:bg-green-700 text-white py-2 px-6 rounded-lg font-semibold"
            >
              ➡️ {t('quiz.fill.next')}
            </button>
          </div>
        )}
      </form>

      <div className="mt-6 text-center text-gray-400 text-sm">
        {t('quiz.fill.score')}: {score} / {shuffledEvents.length}
      </div>
    </div>
  );
};

export default FillInTheBlank;
